import { useParams, Link } from 'react-router-dom';
import { useEffect, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SchemaOrg from '@/components/base/SchemaOrg';
import { articles } from '@/mocks/articles';

function buildArticleSchema(article: typeof articles[0]) {
  const faqSections = article.content.filter((s) => s.type === 'faq');
  const schemas: Record<string, unknown>[] = [];

  schemas.push({
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.summary,
    "image": article.imageUrl,
    "datePublished": "2026-06-29",
    "dateModified": "2026-06-29",
    "author": {
      "@type": "Organization",
      "name": "Pandabuy Spreadsheet System",
      "url": "https://pandabuy.sbs/"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Pandabuy Spreadsheet System",
      "logo": {
        "@type": "ImageObject",
        "url": "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/2ce33a4b04813007a8211abaa4b8148b8a.jpeg"
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://pandabuy.sbs/article/${article.slug}`
    }
  });

  if (faqSections.length > 0) {
    const allFaqItems: { question: string; answer: string }[] = [];
    faqSections.forEach((s) => {
      if (s.faqItems) allFaqItems.push(...s.faqItems);
    });

    if (allFaqItems.length > 0) {
      schemas.push({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": allFaqItems.map((faq) => ({
          "@type": "Question",
          "name": faq.question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": faq.answer
          }
        }))
      });
    }
  }

  return schemas;
}

function ArticleContent({ article }: { article: typeof articles[0] }) {
  return (
    <div className="space-y-8">
      {article.content.map((section, idx) => {
        switch (section.type) {
          case 'heading':
            return (
              <h2
                key={idx}
                className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mt-10 mb-4"
              >
                {section.title}
              </h2>
            );
          case 'paragraph':
            return (
              <p key={idx} className="text-foreground-700 leading-relaxed">
                {section.text}
              </p>
            );
          case 'list':
            return (
              <ul key={idx} className="space-y-2 mt-4">
                {section.items?.map((item, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-foreground-700 text-sm">
                    <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-accent-500"></i>
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            );
          case 'faq':
            return (
              <div key={idx} className="space-y-6 mt-6">
                {section.faqItems?.map((faq, i) => (
                  <div key={i}>
                    <h3 className="font-semibold text-foreground-950 text-base mb-2">
                      {faq.question}
                    </h3>
                    <p className="text-foreground-600 text-sm leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                ))}
              </div>
            );
          case 'table':
            return (
              <div key={idx} className="overflow-x-auto mt-4">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="border-b border-background-300">
                      {section.tableHeaders?.map((h, i) => (
                        <th key={i} className="text-left py-3 pr-4 font-semibold text-foreground-950">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="text-foreground-600">
                    {section.tableRows?.map((row, i) => (
                      <tr key={i} className="border-b border-background-200">
                        {row.map((cell, j) => (
                          <td key={j} className={`py-3 ${j === 0 ? 'pr-4 font-medium text-foreground-800' : j < row.length - 1 ? 'pr-4' : ''}`}>
                            {cell}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          default:
            return null;
        }
      })}
    </div>
  );
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const article = articles.find((a) => a.slug === slug) || articles[0];
  const schemas = useMemo(() => buildArticleSchema(article), [article]);

  return (
    <div className="min-h-screen bg-background-50">
      {schemas.map((s, i) => (
        <SchemaOrg key={i} data={s} />
      ))}
      <Navbar />
      <main>
        {/* Article Hero */}
        <section className="pt-24 md:pt-32 pb-10 md:pb-16 bg-background-50">
          <div className="px-6 md:px-10 lg:px-16">
            <div className="max-w-[800px] mx-auto">
              {/* Breadcrumb */}
              <div className="flex items-center gap-2 mb-6">
                <Link
                  to="/"
                  className="text-foreground-500 hover:text-accent-500 text-sm transition-colors duration-200 flex items-center gap-1"
                >
                  <i className="ri-arrow-left-line"></i>
                  Back to Home
                </Link>
                <span className="text-foreground-300">/</span>
                <span className="text-foreground-400 text-sm">{article.category}</span>
              </div>

              {/* Category Badge */}
              <span className="inline-block px-3 py-1.5 bg-accent-100 text-accent-700 text-xs font-semibold rounded-md mb-5">
                {article.category}
              </span>

              {/* Title */}
              <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight tracking-tight">
                {article.title}
              </h1>

              {/* Meta */}
              <p className="mt-5 text-foreground-500 text-sm md:text-base leading-relaxed">
                Published: June 2026 &bull; {article.readTime} read &bull; By the Pandabuy.sbs Team
              </p>

              {/* Hero Image */}
              <div className="mt-8 rounded-2xl overflow-hidden">
                <img
                  src={article.imageUrl}
                  alt={article.title}
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Article Content */}
        <article className="pb-16 md:pb-20">
          <div className="px-6 md:px-10 lg:px-16">
            <div className="max-w-[800px] mx-auto">
              <ArticleContent article={article} />

              {/* Related Articles */}
              <div className="mt-16 pt-10 border-t border-background-200">
                <h3 className="font-heading text-xl font-bold text-foreground-950 mb-6">
                  More Pandabuy Spreadsheet Guides
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {articles
                    .filter((a) => a.slug !== article.slug)
                    .slice(0, 4)
                    .map((a) => (
                      <Link
                        key={a.slug}
                        to={`/article/${a.slug}`}
                        className="group flex items-start gap-3 p-4 rounded-xl bg-background-100 hover:bg-accent-50 transition-all duration-200 cursor-pointer"
                      >
                        <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
                          <img
                            src={a.imageUrl}
                            alt={a.title}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs text-accent-500 font-medium mb-1">{a.category}</p>
                          <p className="text-sm font-semibold text-foreground-950 line-clamp-2 group-hover:text-accent-500 transition-colors duration-200">
                            {a.title}
                          </p>
                        </div>
                      </Link>
                    ))}
                </div>
              </div>

              {/* CTA */}
              <div className="mt-10 p-8 bg-accent-50 rounded-2xl text-center">
                <h3 className="font-heading text-xl font-bold text-foreground-950 mb-3">
                  Ready to Start Sourcing Smarter?
                </h3>
                <p className="text-foreground-600 text-sm mb-6 max-w-md mx-auto">
                  Explore our curated <strong>pandabuyspreadsheet</strong> categories and start building your own sourcing system today.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <Link
                    to="/"
                    className="whitespace-nowrap px-6 py-3 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-all duration-200 cursor-pointer"
                  >
                    ← Back to Homepage
                  </Link>
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="whitespace-nowrap px-6 py-3 border border-accent-300 text-accent-600 text-sm font-semibold rounded-full hover:bg-accent-100 transition-all duration-200 cursor-pointer"
                  >
                    Pandabuy Sheet →
                  </a>
                </div>
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}